import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import ListSubheader from 'LuxKit/List/ListSubheader'
import { useCallback, useContext, useEffect, useMemo } from 'react'
import { useIntl } from 'react-intl'
import OfferAirportSelectOption from './OfferAirportSelectOption'
import { OfferPageActions } from 'contexts/OfferPage/offerPageStateReducer'
import useAirportSearch from 'hooks/Flights/useAirportSearch'
import { fetchPopularAirports } from 'actions/FlightActions'
import OfferPageDispatchContext from 'contexts/OfferPage/offerPageDispatchContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import noop from 'lib/function/noop'
import styled from 'styled-components'
import ListItemLoadingSkeleton from 'LuxKit/List/ListItemLoadingSkeleton'
import NoAirportsOptionListItem from 'components/Flights/FlightsSearch/SearchWidget/FlightAirportSelect/NoAirportsOptionListItem'
import OfferAirportOptionsGroupedList from './OfferAirportOptionsGroupedList'
import { airportLocationToAirport } from 'lib/flights/flightUtils'
import usePopularAirports from 'hooks/Flights/usePopularAirports'
import config from 'constants/config'

const Wrapper = styled.div`
  position: relative; /* to make the <LoadingIndicator> work  */
`

interface Props {
  searchPhrase?: string
  selectedAirport?: App.Airport
  popularAirports: App.PopularAirports
  onSelectAirport?: (airport: App.AirportLocation) => void
  charterBundleRoute?: App.CharterBundleRoute
}

function OfferAirportOptionsList(props: Props) {
  const {
    searchPhrase = '',
    selectedAirport,
    popularAirports,
    onSelectAirport = noop,
    charterBundleRoute,
  } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()
  const offerPageDispatch = useContext(OfferPageDispatchContext)

  // Get all origin airports for charter bundles
  const originAirports = useAppSelector(state => state.geo.originAirports)
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const popularDepartureAirports = usePopularAirports('departure')
  const airportRanking = useMemo(() => {
    const ranking = new Map<string, number>()
    popularDepartureAirports.forEach((airport, index) => {
      ranking.set(airport.airportCode, index)
    })
    return ranking
  }, [popularDepartureAirports])

  // For charter bundles, show origin airports: AU for Luxury Escapes, AU+NZ for Agent Hub.
  const charterAirports = useMemo(() => {
    if (charterBundleRoute) {
      const isAgentHubNz = config.agentHub.isEnabled && currentRegionCode === 'NZ'
      const allowedCountries = isAgentHubNz ? new Set(['NZ', 'AU']) : new Set(['AU'])
      const countryPriority = (countryCode: string) => (countryCode === 'NZ' ? 0 : 1)

      return originAirports
        .filter(airport => !!airport.countryCode && allowedCountries.has(airport.countryCode))
        .slice()
        .sort((a, b) => {
          if (a.code === 'MEL') {
            return -1
          }

          if (b.code === 'MEL') {
            return 1
          }

          if (isAgentHubNz && a.countryCode !== b.countryCode) {
            const countryA = a.countryCode ?? ''
            const countryB = b.countryCode ?? ''
            return countryPriority(countryA) - countryPriority(countryB)
          }

          const rankA = airportRanking.get(a.code)
          const rankB = airportRanking.get(b.code)

          if (rankA !== undefined && rankB !== undefined) {
            return rankA - rankB
          }

          if (rankA !== undefined) {
            return -1
          }

          if (rankB !== undefined) {
            return 1
          }

          return a.name.localeCompare(b.name)
        })
    }
    return []
  }, [charterBundleRoute, currentRegionCode, originAirports, airportRanking])

  useEffect(() => {
    dispatch(fetchPopularAirports())
  }, [dispatch])

  const [searchResults, loading] = useAirportSearch(searchPhrase)

  // Convert AirportLocation[] to Airport[] for the grouped list component
  const airports = useMemo(() => {
    return searchResults.map(airport => ({
      code: airport.airportCode,
      name: airport.airportName,
      latitude: airport.lt!,
      longitude: airport.lg!,
      countryCode: airport.country,
    } as App.Airport))
  }, [searchResults])

  const handleItemClick = useCallback((airport: App.AirportLocation) => {
    offerPageDispatch({
      type: OfferPageActions.setAirport,
      airport: airportLocationToAirport(airport),
    })
    offerPageDispatch({
      type: OfferPageActions.setFlights,
      flights: true,
    })
    onSelectAirport(airport)
  }, [offerPageDispatch, onSelectAirport])

  const auNzAirportsLabel = intl.formatMessage({
    id: '4kF5r04n4agxBExYVX1j',
    defaultMessage: 'Australia & New Zealand airports',
    description: 'Section header for Australian and New Zealand airports in airport selection list',
  })
  const auAirportsLabel = intl.formatMessage({
    id: 'q-RHgmPat4jGhT0u7EXg',
    defaultMessage: 'Australian airports',
    description: 'Section header for Australian airports in airport selection list',
  })
  const nearbyAirportsLabel = intl.formatMessage({
    id: '7-9vb1SVZ5CruSKphF1m',
    defaultMessage: 'Airports near you',
    description: 'Section header for nearby airports in airport selection list',
  })

  const airportListLabel = charterBundleRoute
    ? (config.agentHub.isEnabled && currentRegionCode === 'NZ' ? auNzAirportsLabel : auAirportsLabel)
    : nearbyAirportsLabel

  const airportsList = charterBundleRoute ? (
    <OfferAirportOptionsGroupedList
      airports={charterAirports}
      handleItemClick={handleItemClick}
      selectedAirport={selectedAirport}
    />
  ) : (
    popularAirports.origins.map(airport => <OfferAirportSelectOption
      key={airport.airportCode}
      airport={airport}
      onClick={handleItemClick}
      selected={airport.airportCode === selectedAirport?.code}
      data-testid={`airport-select-${airport.airportCode}`}
    />)
  )

  return <Wrapper>
    <LoadingIndicator floating visible={loading} />
    {searchPhrase.length < 2 && <VerticalSpacer gap={8}>
      <ListSubheader colour="neutral-three">{airportListLabel}</ListSubheader>
      <div>
        {airportsList}
      </div>
    </VerticalSpacer>}

    {loading && !airports.length && <>
      {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
      <ListItemLoadingSkeleton size="large" title="8ch" subtitle="3ch" startIcon />
    </>}

    {!loading && !airports.length && !!searchPhrase && <NoAirportsOptionListItem inputValue={searchPhrase} />}

    {searchPhrase.length > 1 && <OfferAirportOptionsGroupedList
      airports={airports}
      handleItemClick={handleItemClick}
      selectedAirport={selectedAirport}
    />}
  </Wrapper>
}

export default withOfferPageAndReduxState((state, appState) => ({
  popularAirports: appState.flights.popularAirports,
  selectedAirport: state.airport,
  charterBundleRoute: state.charterBundleRoute,
}), OfferAirportOptionsList)
